import { IndexedDBRepository, ObjectStoreDefinition } from 'helpers/IndexedDBRepository';
import {
  ChannelMessage,
  createBroadcastChannel,
  NetworkEventDataMap,
  NetworkEventType,
} from 'root/apps/NetworkHealthTool/utils/channelUtils';

// Speed metrics
export interface NetworkSpeedMetrics {
  download?: number;
  upload?: number;
  latency?: number;
  jitter?: number;
  downLoadedLatency?: number;
  downLoadedJitter?: number;
  upLoadedLatency?: number;
  upLoadedJitter?: number;
  packetLoss?: number;
}

export const ClassificationNames = {
  BAD: 'bad',
  POOR: 'poor',
  AVERAGE: 'average',
  GOOD: 'good',
  GREAT: 'great',
} as const;
export type ClassificationName = (typeof ClassificationNames)[keyof typeof ClassificationNames];

// Scoring
export interface Score {
  classificationIdx: 0 | 1 | 2 | 3 | 4;
  classificationName: ClassificationName;
  points: number;
}

export type ScoreResults = {
  [category: string | 'rtc']: Score;
};

export const NamedEvent = {
  NETWORK_DEGRADATION_STARTED: 'network_degradation_started',
  NETWORK_RECOVERED: 'network_recovered',
  MANUAL_NETWORK_TEST: 'manual_network_test',
} as const;

export type NamedEvent = (typeof NamedEvent)[keyof typeof NamedEvent];

// Entity stored in IndexedDB
export interface NetworkTestEntity extends NetworkSpeedMetrics {
  id: number;
  accountId: string;
  shopId: string;
  employeeId: string;
  registerId: string;
  timestamp: number;
  scores: ScoreResults;
  userAgent: string;
  pageUrl: string;
  namedEvent?: NamedEvent;
  syncTimestamp?: number; // Optional field for sync timestamp
}

export type NewNetworkTestEntity = Omit<NetworkTestEntity, 'id'>;

export const RETENTION_PERIOD_THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
export const LAST_RUN_TIME_ONE_WEEK = 7 * 24 * 60 * 60 * 1000;

export class NetworkHealthToolService {
  private readonly databaseName = 'NetworkHealthTestRepository';
  private readonly databaseVersion = 2;
  private readonly objectStoreName = 'NetworkTestResults';

  private readonly repository: IndexedDBRepository<NetworkTestEntity>;
  private readonly accountId: string;
  private listeners: { [event: string]: Array<(data: any) => void> } = {};

  private readonly channel: BroadcastChannel;

  constructor(accountId?: string) {
    this.accountId = String(accountId ?? window.merchantos?.account?.id ?? '');
    this.repository = this.createRepository();
    this.listeners = {};
    this.channel = createBroadcastChannel();
  }

  private createRepository(): IndexedDBRepository<NetworkTestEntity> {
    const definition: ObjectStoreDefinition = {
      name: this.objectStoreName,
      options: { keyPath: 'id', autoIncrement: true },
      indexes: [
        {
          name: 'accountId',
          keyPath: ['accountId'],
          options: { unique: false },
        },
        {
          name: 'timestamp',
          keyPath: ['timestamp'],
          options: { unique: false },
        },
        {
          name: 'namedEvent',
          keyPath: ['namedEvent'],
          options: { unique: false },
        },
        {
          name: 'syncTimestamp',
          keyPath: ['syncTimestamp'],
          options: { unique: false },
        },
      ],
    };

    return new IndexedDBRepository(this.databaseName, this.databaseVersion, definition);
  }

  // Create full entity from raw speed metrics
  private createEntityFromSpeedTest(
    data: NetworkSpeedMetrics,
    scores: ScoreResults,
    namedEvent?: NetworkTestEntity['namedEvent']
  ): NewNetworkTestEntity {
    const session = window.merchantos?.session;

    const entity: NewNetworkTestEntity = {
      ...data,
      shopId: String(session?.getShop?.()?.shopID ?? 'Unknown Shop ID'),
      accountId: this.accountId,
      employeeId: String(session?.getEmployee?.()?.employeeID ?? 'Unknown Employee ID'),
      registerId: String(session?.sessData?.Register?.registerID ?? 'Unknown Register ID'),
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      pageUrl: window.location.href,
      scores,
    };
    if (namedEvent) entity.namedEvent = namedEvent;

    return entity;
  }

  // Add a new test to indexDB
  public async addNetworkTest(
    metrics: NetworkSpeedMetrics,
    scores: ScoreResults,
    namedEvent?: NamedEvent
  ): Promise<NetworkTestEntity> {
    const entity = this.createEntityFromSpeedTest(metrics, scores, namedEvent);
    const key = await this.repository.add(entity);
    const addedEntity = await this.repository.get(key);
    this.emit(NetworkEventType.NETWORK_TEST_ADDED, addedEntity);
    this.broadcastAdded(addedEntity);
    return addedEntity;
  }

  public async updateNetworkTest(
    id: NetworkTestEntity['id'],
    entity: Partial<NetworkTestEntity>
  ): Promise<NetworkTestEntity | null> {
    const existingEntity = await this.repository.get(id);
    if (!existingEntity) {
      throw new Error(`Network test with ID ${id} does not exist`);
    }

    if (existingEntity.accountId !== this.accountId) {
      throw new Error(`Cannot update a record from a different account`);
    }

    // Update the last test with the current timestamp
    const updatedEntity: NetworkTestEntity = {
      ...existingEntity,
      ...entity,
      id,
    };

    await this.repository.update(updatedEntity);
    this.emit(NetworkEventType.NETWORK_TEST_UPDATED, updatedEntity);
    return updatedEntity;
  }

  // Fetch all test results
  public getAllNetworkTests(): Promise<NetworkTestEntity[]> {
    return this.repository.getByIndex('accountId', [this.accountId]);
  }

  public async getTimeSinceLastTest(): Promise<number | null> {
    const last = await this.getLastNetworkTest();
    if (!last) return null; // never ran
    return Date.now() - last.timestamp;
  }

  // Fetch the latest test result
  public async getLastNetworkTest(): Promise<NetworkTestEntity | null> {
    const networkTests = await this.getLastNetworkTests(1);
    if (networkTests === null || networkTests.length === 0) {
      return null;
    }
    return networkTests[0];
  }

  public async getLastNetworkTests(count = 1): Promise<NetworkTestEntity[] | null> {
    const all = await this.repository.getWhere((entity) => entity.accountId === this.accountId, {
      sort: 'prev',
      sortIndex: 'timestamp',
      limit: count,
    });
    return all.length > 0 ? all : null;
  }

  public async deleteOldNetworkTests(): Promise<void> {
    const now = Date.now();

    const checkedLastRunTime = localStorage.getItem('networkTestRetentionLastRun');
    const lastRun = Number(checkedLastRunTime ?? '0');
    if (!isNaN(lastRun) && now - lastRun < LAST_RUN_TIME_ONE_WEEK) return;

    await this.repository.deleteWhere(
      (test) =>
        test.accountId === this.accountId && now - test.timestamp > RETENTION_PERIOD_THIRTY_DAYS
    );

    localStorage.setItem('networkTestRetentionLastRun', String(now));
  }

  public async getAllUnsyncedNetworkTests(): Promise<NetworkTestEntity[] | null> {
    const all = await this.repository.getWhere(
      (entity) =>
        entity.accountId === this.accountId &&
        entity.namedEvent !== undefined &&
        entity.syncTimestamp === undefined,
      {
        sort: 'prev',
        sortIndex: 'timestamp',
      }
    );
    return all.length > 0 ? all : null;
  }

  public closeConnection(): void {
    this.repository.closeDatabase();
  }

  /**
   * Broadcast channel management
   * =======================================================================
   */
  private broadcastAdded(entity: NetworkTestEntity): void {
    const message: ChannelMessage = {
      type: NetworkEventType.NETWORK_TEST_ADDED,
      payload: entity,
    };
    this.channel.postMessage(message);
  }

  /**
   * Event management
   * =======================================================================
   */
  public on<K extends NetworkEventType>(
    event: K,
    listener: (data: NetworkEventDataMap[K]) => void
  ): void {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(listener);
  }

  public off(event: NetworkEventType, listener: (data: any) => void): void {
    if (!this.listeners[event]) return;
    this.listeners[event] = this.listeners[event].filter((l) => l !== listener);
  }

  private emit<K extends NetworkEventType>(event: K, data: NetworkEventDataMap[K]): void {
    if (!this.listeners[event]) return;
    this.listeners[event].forEach((listener) => listener(data));
  }
}
